import { Component, OnInit, Input, EventEmitter, Output, ElementRef, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { debounceTime } from 'rxjs/operators';
import { IInputProps, IButtonProps, ButtonVariant, InputVariant } from '@amobee/component-library';

// Item of this type will be emited when it is selected
export interface AutoCompleteMappedItem {
  label: string;
  value: string | number;
  disabled?: boolean;
}

// This type will need to return from mapper function
export interface AutoCompleteMappedResponse {
  results: AutoCompleteMappedItem[],
  hasMore: boolean
}

@Component({
  selector: 'auto-complete',
  templateUrl: './auto-complete.component.html',
  styleUrls: ['./auto-complete.component.scss']
})
export class AutoCompleteComponent implements OnInit {

  @Input('endpointUrl') public endpointUrl?: string;
  @Input('fetchData') public fetchData?: (params: object) => Promise<any>;
  @Input('width') public width?: string = '300px';
  @Input('height') public height?: string = '300px';
  @Input('buttonTrigger') public buttonTrigger?: boolean = false;
  @Input('limit') public limit?: number = 25;
  @Input('responseMapper') public responseMapper?: (response: any, offset: number, limit: number, searchString?: string) => AutoCompleteMappedResponse;
  @Input() public set initialValue(inputValue: string) {
    this.searchString.patchValue(inputValue, { emitEvent: false });
    this.searchStringValue = inputValue;
  }
  public _disabled: boolean = false;
  @Input() public set disabled(value: boolean) {
    this._disabled = value;
    if (value) {
      this.searchString.disable();
    } else {
      this.searchString.enable();
    }
  }

  @Output() public onSelect = new EventEmitter<AutoCompleteMappedItem>();
  @Output() public onClickOutside = new EventEmitter<String>();

  public buttonProps: IButtonProps = {
    variant: ButtonVariant.STATIC_BORDERLESS,
    compact: true,
    dark: true,
    icon: 'ChevronDown'
  }

  @Input('searchProps') public searchProps?: IInputProps = {
    compact: false,
    variant: InputVariant.TABLE,
    leftElement: 'Search',
    placeholder: 'Search',
    fluid: true,
    dark: true
  };

  public searchString = new FormControl();

  @ViewChild('autoCompleteWrapper', { static: true }) parentDiv: ElementRef;

  public showMenu: boolean = false;

  public offset: number = 0;

  public hasMore: boolean = false;

  public items: any[] = null;

  public searchStringValue: string = '';

  constructor(private http: HttpClient) { }

  ngOnInit() {
    this.searchString.valueChanges.pipe(debounceTime(500)).subscribe((value) => {
      this.searchStringValue = value;
      this.offset = 0;
      this.items = [];
      this.fetchList();
    });
  }

  openMenu() {
    this.showMenu = true;
    if (!this.buttonTrigger) {
      this.offset = 0;
      this.items = [];
      this.fetchList();
    }
  }

  handleOnScroll() {
    if (this.hasMore) {
      this.offset += this.limit;
      this.fetchList();
    }
  }

  handleOnItemClick(item: AutoCompleteMappedItem) {
    this.showMenu = false;
    this.onSelect.emit(item);
  }

  handleOnButtonClick() {
    this.showMenu = !this.showMenu;
    if (this.showMenu && !this.items) {
      this.fetchList();
    }
  }

  closeMenu($event) {
    if (!this.parentDiv.nativeElement.contains($event.target)) {
      this.showMenu = false;
      this.onClickOutside.emit(this.searchStringValue);
    }
  }

  defaultResponseMapper(response: any, offset: number, limit: number, searchString?: string): AutoCompleteMappedResponse {
    // if responseMapper is not provided, will use this default one
    let items: AutoCompleteMappedItem[] = [];
    let results = response.results || response;
    if (results) {
      items = results.map((item) => {
        return {
          label: item.name || item.label,
          value: item.objectId || item.value || item.name,
          disabled: item.disabled
        }
      });
    }
    let hasMore = (response.count && response.count > offset + limit) || response.hasMore;
    return {
      results: items,
      hasMore: !!hasMore
    }
  }

  fetchList() {
    let params = {
      searchString: this.searchStringValue,
      limit: this.limit.toString(),
      offset: this.offset.toString()
    };

    let promise: Promise<any>;
    if (this.endpointUrl) {
      promise = this.http.get(this.endpointUrl, { params: params }).toPromise();
    } else {
      promise = this.fetchData(params);
    }

    promise.then((response: any) => {
      let mappedResponse: AutoCompleteMappedResponse;
      if (this.responseMapper) {
        mappedResponse = this.responseMapper(response, this.offset, this.limit, params.searchString);
      } else {
        mappedResponse = this.defaultResponseMapper(response, this.offset, this.limit, params.searchString);
      }
      if (this.offset == 0) {
        this.items = mappedResponse.results;
      } else {
        this.items = [...this.items, ...mappedResponse.results];
      }
      this.hasMore = mappedResponse.hasMore;
    }).catch((err) => {
      if (this.offset == 0) {
        this.items = [];
      }
      this.hasMore = false;
    });
  }
}
